import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
  Paper,
  Divider,
  InputAdornment,
  Alert,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Stack,
  alpha
} from '@mui/material';
import {
  LocalOffer as LocalOfferIcon,
  Save as SaveIcon,
  HelpOutline as HelpIcon,
  CheckCircle as CheckCircleIcon,
  Category as CategoryIcon,
  Discount as DiscountIcon,
  Inventory as InventoryIcon,
  Close as CloseIcon,
  Collections as CollectionsIcon,
  Palette as PaletteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import { fetchCategories } from '../categoriesApi';
import { fetchProductsByCategory, createOffer } from '../offersApi';
import { useBranch } from '../BranchContext';
import { useTranslation } from 'react-i18next';

const Offers = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [discount, setDiscount] = useState(0);
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const { selectedBranch } = useBranch();

  // ✅ FIX: Track custom prices - Key: productId, Value: number or string
  const [customPrices, setCustomPrices] = useState({});

  useEffect(() => {
    loadCategories();
    // eslint-disable-next-line
  }, [selectedBranch]);

  const loadCategories = async () => {
    if (!selectedBranch) return;
    try {
      const data = await fetchCategories(selectedBranch.id || selectedBranch.Id);
      const filtered = data.filter(c => {
        const name = c.categoryName || c.CategoryName || '';
        return !name.startsWith("العروض:") && 
               !name.startsWith("مجموعة:") &&
               name !== "Offer" &&
               name !== "Deal";
      });
      setCategories(filtered);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const handleCategoryClick = async (category) => {
    setSelectedCategory(category);
    setSelectedProductIds([]);
    setProducts([]);
    setCustomPrices({});
    setLoading(true);
    try {
      const branchId = selectedBranch ? (selectedBranch.id || selectedBranch.Id) : null;
      const data = await fetchProductsByCategory(category.id || category.Id, branchId);
      const targetCategoryId = category.id || category.Id;
      const filteredProducts = (data || []).filter(p => (p.categoryId || p.CategoryId) === targetCategoryId && (!Array.isArray(p.SubProducts) || p.SubProducts.length === 0));
      setProducts(filteredProducts);
    } catch (error) {
      console.error('Error loading products:', error);
      Swal.fire(t('error'), t('failedToLoadProducts'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleProductToggle = (productId) => {
    setSelectedProductIds(prev => {
      if (prev.includes(productId)) {
        setCustomPrices(prevPrices => {
          const updated = { ...prevPrices };
          delete updated[productId];
          return updated;
        });
        return prev.filter(id => id !== productId);
      } else {
        return [...prev, productId];
      }
    });
  };

  // ✅ Calculate discount-based price
  const calculateNewPrice = (oldPrice) => {
    const price = Number(oldPrice) || 0;
    const disc = Number(discount) || 0;
    return (price * (1 - disc / 100)).toFixed(2);
  };

  // ✅ FIX: Get the effective new price for a product
  // Returns the custom price if manually edited, otherwise the calculated price
  const getEffectiveNewPrice = (product) => {
    const productId = product.id || product.Id;
    const oldPrice = product.oldPrice || product.OldPrice || product.price || product.Price || 0;
    
    // ✅ FIX: Check if customPrices has this productId AND value is not empty/undefined
    if (productId in customPrices && customPrices[productId] !== '' && customPrices[productId] !== undefined && customPrices[productId] !== null) {
      return String(customPrices[productId]);
    }
    return calculateNewPrice(oldPrice);
  };

  // ✅ FIX: Handle manual price edit - store as the raw value
  const handleCustomPriceChange = (productId, value) => {
    setCustomPrices(prev => ({
      ...prev,
      [productId]: value
    }));
  };

  // ✅ Reset a product's price back to the discount-calculated price
  const resetToCalculatedPrice = (product) => {
    const productId = product.id || product.Id;
    setCustomPrices(prev => {
      const updated = { ...prev };
      delete updated[productId];
      return updated;
    });
  };

  // ✅ FIX: Check if a product has a custom price
  const hasCustomPrice = (productId) => {
    return productId in customPrices && customPrices[productId] !== '' && customPrices[productId] !== undefined && customPrices[productId] !== null;
  };

  const getProductImagesCount = (product) => {
    const productImages = product.productImages || product.ProductImages;
    if (!productImages) return 0;
    if (Array.isArray(productImages)) return productImages.length;
    return 0;
  };

  const getProductColorsCount = (product) => {
    const productColors = product.productColors || product.ProductColors;
    if (!productColors) return 0;
    if (Array.isArray(productColors)) return productColors.length;
    return 0;
  };

  const getTotalProductImagesCount = () => {
    let total = 0;
    const selectedProds = products.filter(p => selectedProductIds.includes(p.id || p.Id));
    selectedProds.forEach(p => {
      total += getProductImagesCount(p);
    });
    return total;
  };

  const getTotalProductColorsCount = () => {
    let total = 0;
    const selectedProds = products.filter(p => selectedProductIds.includes(p.id || p.Id));
    selectedProds.forEach(p => {
      total += getProductColorsCount(p);
    });
    return total;
  };

const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedBranch) {
      Swal.fire(t('warning'), t('selectBranchFirst'), 'warning');
      return;
    }
    if (!selectedCategory || selectedProductIds.length === 0 || discount === '') {
      Swal.fire(t('warning'), t('offersValidationMessage'), 'warning');
      return;
    }

    const discountValue = Number(discount);
    
    console.log('═══════════════════════════════════════');
    console.log('🚀 SUBMITTING OFFER');
    console.log('💰 Discount value:', discountValue, 'type:', typeof discountValue);
    console.log('📦 Selected product IDs:', selectedProductIds);
    console.log('✏️ Custom prices map:', JSON.stringify(customPrices));
    console.log('═══════════════════════════════════════');

    const selectedProductsData = products
      .filter(p => selectedProductIds.includes(p.id || p.Id))
      .map(p => {
        const productId = p.id || p.Id;
        const oldPrice = Number(p.oldPrice || p.OldPrice || p.price || p.Price || 0);
        
        let finalNewPrice;
        if (productId in customPrices && customPrices[productId] !== '' && customPrices[productId] !== undefined && customPrices[productId] !== null) {
          finalNewPrice = Number(customPrices[productId]);
        } else {
          finalNewPrice = Number(calculateNewPrice(oldPrice));
        }

        console.log(`📦 Product: "${p.productName || p.ProductName}"`);
        console.log(`   ID: ${productId}`);
        console.log(`   Old Price: ${oldPrice}`);
        console.log(`   New Price: ${finalNewPrice}`);
        console.log(`   Has Custom: ${hasCustomPrice(productId)}`);
        console.log(`   Discount would give: ${(oldPrice * (1 - discountValue / 100)).toFixed(2)}`);

        // ✅ FIX: Send with BOTH casings to be safe
        return {
          ProductId: productId,
          productId: productId,
          OldPrice: oldPrice,
          oldPrice: oldPrice,
          NewPrice: finalNewPrice,
          newPrice: finalNewPrice
        };
      });

    const payload = {
      CategoryId: selectedCategory.id || selectedCategory.Id,
      categoryId: selectedCategory.id || selectedCategory.Id,
      Discount: discountValue,
      discount: discountValue,
      Products: selectedProductsData,
      products: selectedProductsData,
      BranchId: selectedBranch.id || selectedBranch.Id,
      branchId: selectedBranch.id || selectedBranch.Id
    };

    console.log('📤 FULL PAYLOAD:', JSON.stringify(payload, null, 2));

    try {
      const response = await createOffer(payload);
      if (response.success || response.Success) {
        const totalImages = getTotalProductImagesCount();
        const totalColors = getTotalProductColorsCount();
        
        let successMessage = t('offerCreatedSuccess');
        let details = [];
        
        if (totalImages > 0) {
          details.push(`📸 ${totalImages} ${t('productImagesIncluded') || 'product images'}`);
        }
        if (totalColors > 0) {
          details.push(`🎨 ${totalColors} ${t('productColorsIncluded') || 'product colors'}`);
        }
        
        const customCount = selectedProductsData.filter((_, idx) => {
          const prod = products.filter(p => selectedProductIds.includes(p.id || p.Id))[idx];
          return prod && hasCustomPrice(prod.id || prod.Id);
        }).length;
        
        if (customCount > 0) {
          details.push(`✏️ ${customCount} ${t('productsWithCustomPrice') || 'products with manually edited prices'}`);
        }
        
        if (details.length > 0) {
          successMessage += '\n\n' + details.join('\n') + '\n' + (t('includedAutomatically') || 'included automatically');
        }
        
        Swal.fire(t('success'), successMessage, 'success');
        setSelectedCategory(null);
        setProducts([]);
        setSelectedProductIds([]);
        setDiscount(0);
        setCustomPrices({});
      } else {
        Swal.fire(t('error'), response.errorMessage || response.ErrorMessage || t('createOfferError'), 'error');
      }
    } catch (error) {
      console.error('❌ Create offer error:', error);
      Swal.fire(t('error'), t('createOfferError'), 'error');
    }
  };
  
  const HelpDialog = () => {
    const steps = [
      {
        label: t('helpOffersStep1Label') || 'Step 1: Select Category',
        icon: <CategoryIcon color="primary" />,
        description: t('helpOffersStep1Description') || 'Choose a category to apply offers',
        tips: [
          t('helpOffersStep1Tip1') || 'Click any category card',
          t('helpOffersStep1Tip2') || 'Products will load automatically',
          t('helpOffersStep1Tip3') || 'Only one category at a time'
        ]
      },
      {
        label: t('helpOffersStep2Label') || 'Step 2: Set Discount',
        icon: <DiscountIcon color="primary" />,
        description: t('helpOffersStep2Description') || 'Enter discount percentage',
        tips: [
          t('helpOffersStep2Tip1') || 'Enter 0-100%',
          t('helpOffersStep2Tip2') || 'Discount applies to all selected products',
          t('helpOffersStep2Tip3') || 'Preview shows new price',
          t('helpOffersStep2Tip4') || 'You can manually edit the new price per product'
        ]
      },
      {
        label: t('helpOffersStep3Label') || 'Step 3: Select Products',
        icon: <InventoryIcon color="primary" />,
        description: t('helpOffersStep3Description') || 'Choose products for the offer',
        tips: [
          t('helpOffersStep3Tip1') || 'Check boxes to select',
          t('helpOffersStep3Tip2') || 'Select multiple products',
          t('helpOffersStep3Tip3') || 'See old vs new price',
          t('helpOffersStep3Tip4') || 'Products with 📷 icon have additional images',
          t('helpOffersStep3Tip5') || 'Products with 🎨 icon have multiple colors',
          t('helpOffersStep3Tip6') || 'Minimum 1 product required'
        ]
      },
      {
        label: t('helpOffersStep4Label') || 'Step 4: Apply Offer',
        icon: <SaveIcon color="success" />,
        description: t('helpOffersStep4Description') || 'Save and activate the offer',
        tips: [
          t('helpOffersStep4Tip1') || 'Review all selections',
          t('helpOffersStep4Tip2') || 'Offer goes live immediately',
          t('helpOffersStep4Tip3') || 'Product images & colors copied automatically',
          t('helpOffersStep4Tip4') || 'Can be edited later'
        ]
      }
    ];

    return (
      <Dialog 
        open={helpOpen} 
        onClose={() => setHelpOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        sx={{
          '& .MuiDialog-container': {
            alignItems: 'flex-start',
            paddingTop: { xs: '100px', sm: '80px', md: '100px' }
          }
        }}
        PaperProps={{
          sx: { 
            borderRadius: isMobile ? 0 : 2,
            maxHeight: { xs: 'calc(100vh - 200px)', sm: 'calc(100vh - 160px)', md: 'calc(100vh - 200px)' },
            overflowY: 'auto'
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: 'primary.main', 
          color: 'white', 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          p: { xs: 2, sm: 2.5 }
        }}>
          <HelpIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
          <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            {t('helpOffersDialogTitle') || 'How to Create Offers'}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <IconButton 
            onClick={() => setHelpOpen(false)} 
            sx={{ color: 'white' }}
            size={isMobile ? "small" : "medium"}
          >
            <CloseIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ mt: 2, p: { xs: 2, sm: 3 } }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography 
              variant="body2" 
              fontWeight="bold"
              sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}
            >
              {t('helpWhatIsOffer') || 'What is an Offer?'}
            </Typography>
            <Typography 
              variant="body2"
              sx={{ fontSize: { xs: '0.8rem', sm: '0.85rem' } }}
            >
              {t('helpWhatIsOfferDesc') || 'An offer applies a discount to multiple products in a category at once. Product images and colors are automatically included.'}
            </Typography>
          </Alert>

          <List sx={{ width: '100%' }}>
            {steps.map((step, index) => (
              <Box key={index}>
                <ListItem alignItems="flex-start" sx={{ py: { xs: 1.5, sm: 2 }, px: { xs: 0, sm: 2 } }}>
                  <ListItemIcon sx={{ mt: 1, minWidth: { xs: 40, sm: 56 } }}>
                    <Box sx={{ 
                      bgcolor: 'primary.light', 
                      borderRadius: '50%', 
                      p: { xs: 1, sm: 1.5 }, 
                      display: 'flex',
                      boxShadow: 1
                    }}>
                      {React.cloneElement(step.icon, { sx: { fontSize: { xs: 18, sm: 24 } } })}
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography 
                        variant="h6" 
                        fontWeight="bold" 
                        color="primary" 
                        sx={{ 
                          mb: 1,
                          fontSize: { xs: '0.95rem', sm: '1.1rem' }
                        }}
                      >
                        {step.label}
                      </Typography>
                    }
                    secondary={
                      <Box>
                        <Typography 
                          variant="body2" 
                          color="text.secondary" 
                          sx={{ 
                            mb: 2,
                            fontSize: { xs: '0.8rem', sm: '0.85rem' }
                          }}
                        >
                          {step.description}
                        </Typography>
                        <Box sx={{ bgcolor: '#f5f5f5', p: { xs: 1.5, sm: 2 }, borderRadius: 1 }}>
                          <Typography 
                            variant="caption" 
                            fontWeight="bold" 
                            color="primary" 
                            sx={{ 
                              mb: 1, 
                              display: 'block',
                              fontSize: { xs: '0.75rem', sm: '0.8rem' }
                            }}
                          >
                            💡 {t('helpTips') || 'Tips'}
                          </Typography>
                          {step.tips.map((tip, idx) => (
                            <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5 }}>
                              <CheckCircleIcon sx={{ 
                                fontSize: { xs: 14, sm: 16 }, 
                                mr: 1, 
                                color: 'success.main', 
                                mt: 0.3 
                              }} />
                              <Typography 
                                variant="body2"
                                sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }}
                              >
                                {tip}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    }
                  />
                </ListItem>
                {index < steps.length - 1 && <Divider />}
              </Box>
            ))}
          </List>

          <Alert severity="warning" sx={{ mt: 3 }}>
            <Typography 
              variant="body2" 
              fontWeight="bold"
              sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}
            >
              ⚠️ {t('helpImportantNotes') || 'Important Notes'}
            </Typography>
            <Typography 
              variant="body2" 
              component="div"
              sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }}
            >
              <Box sx={{ mt: 1 }}>
                • {t('helpOffersImportantNote1') || 'Offers apply to entire categories'}<br/>
                • {t('helpOffersImportantNote2') || 'Can select multiple products'}<br/>
                • {t('helpOffersImportantNote3') || 'Discount percentage is uniform'}<br/>
                • {t('helpOffersImportantNote4') || 'Product images & colors copied automatically'}<br/>
                • {t('helpOffersImportantNote5') || 'Changes are immediate'}
              </Box>
            </Typography>
          </Alert>
        </DialogContent>

        <DialogActions sx={{ p: { xs: 2, sm: 2.5 }, bgcolor: '#f9f9f9' }}>
          <Button 
            onClick={() => setHelpOpen(false)} 
            variant="contained" 
            color="primary" 
            fullWidth={isMobile}
            sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}
          >
            {t('helpGotIt') || 'Got it!'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
      <HelpDialog />

      {/* Header */}
      <Stack 
        direction={{ xs: 'column', sm: 'row' }} 
        alignItems={{ xs: 'flex-start', sm: 'center' }} 
        justifyContent="space-between"
        spacing={{ xs: 2, sm: 0 }}
        mb={{ xs: 2.5, sm: 3 }}
      >
        <Stack direction="row" alignItems="center" spacing={{ xs: 1.5, sm: 2 }} flexWrap="wrap">
          <LocalOfferIcon sx={{ fontSize: { xs: 32, sm: 40 }, color: 'primary.main' }} />
          <Typography 
            variant="h4"
            sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }}
          >
            {t('offersAndDeals') || 'Offers & Deals'}
          </Typography>
          {selectedCategory && (
            <Chip 
              label={selectedCategory.categoryName || selectedCategory.CategoryName} 
              color="primary" 
              icon={<CategoryIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />}
              onDelete={() => {
                setSelectedCategory(null);
                setProducts([]);
                setSelectedProductIds([]);
                setDiscount(0);
                setCustomPrices({});
              }}
              size="small"
              sx={{ 
                fontSize: { xs: '0.75rem', sm: '0.85rem' },
                height: { xs: 26, sm: 32 }
              }}
            />
          )}
        </Stack>
        <Tooltip title={t('helpButtonTooltip') || 'Click for help'} arrow>
          <Button
            variant="contained"
            color="info"
            startIcon={<HelpIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />}
            onClick={() => setHelpOpen(true)}
            fullWidth={isMobile}
            size={isMobile ? "medium" : "large"}
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 'bold',
              fontSize: { xs: '0.85rem', sm: '0.9rem' },
              boxShadow: 3,
              '&:hover': {
                boxShadow: 6
              }
            }}
          >
            {t('helpButtonText') || 'Help'}
          </Button>
        </Tooltip>
      </Stack>

      {!selectedBranch && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {t('pleaseSelectBranchOffers') || 'Please select a branch first'}
        </Alert>
      )}

      {/* Show ProductImages & ProductColors info */}
      {selectedProductIds.length > 0 && (getTotalProductImagesCount() > 0 || getTotalProductColorsCount() > 0) && (
        <Stack spacing={2} sx={{ mb: 3 }}>
          {getTotalProductImagesCount() > 0 && (
            <Alert severity="info" icon={<CollectionsIcon />}>
              <Typography variant="body2" fontWeight="bold">
                📸 {getTotalProductImagesCount()} {t('productImagesWillBeCopied') || 'product images will be copied to the offer automatically'}
              </Typography>
            </Alert>
          )}
          
          {getTotalProductColorsCount() > 0 && (
            <Alert severity="success" icon={<PaletteIcon />}>
              <Typography variant="body2" fontWeight="bold">
                🎨 {getTotalProductColorsCount()} {t('productColorsWillBeCopied') || 'product colors will be copied to the offer automatically'}
              </Typography>
            </Alert>
          )}
        </Stack>
      )}

      <Card elevation={3} sx={{ borderRadius: { xs: 2, sm: 3 } }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          {/* Category Selection */}
          <Box sx={{ mb: 4 }}>
            <Paper 
              elevation={0}
              sx={{ 
                p: { xs: 1.5, sm: 2 }, 
                bgcolor: alpha(theme.palette.primary.main, 0.05),
                borderLeft: '4px solid',
                borderColor: 'primary.main',
                mb: { xs: 2, sm: 3 }
              }}
            >
              <Typography 
                variant="h6" 
                gutterBottom 
                color="primary" 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  fontSize: { xs: '1rem', sm: '1.25rem' }
                }}
              >
                <CategoryIcon sx={{ fontSize: { xs: 20, sm: 24 } }} /> 
                {t('selectCategoryStep') || 'Step 1: Select Category'}
              </Typography>
            </Paper>
            
            {categories.length === 0 ? (
              <Alert severity="info">{t('noCategoriesFound') || 'No categories found'}</Alert>
            ) : (
              <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                {categories.map((cat) => (
                  <Grid item xs={6} sm={4} md={3} lg={2} key={cat.id || cat.Id}>
                    <Paper
                      elevation={selectedCategory?.id === cat.id ? 6 : 2}
                      sx={{
                        p: { xs: 1.5, sm: 2 },
                        cursor: 'pointer',
                        bgcolor: selectedCategory?.id === cat.id ? 'primary.main' : 'background.paper',
                        color: selectedCategory?.id === cat.id ? 'primary.contrastText' : 'text.primary',
                        textAlign: 'center',
                        minHeight: { xs: 70, sm: 80 },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.3s ease',
                        borderRadius: { xs: 2, sm: 3 },
                        border: selectedCategory?.id === cat.id ? '2px solid' : '1px solid transparent',
                        borderColor: 'primary.dark',
                        '&:hover': { 
                          transform: isMobile ? 'none' : 'translateY(-4px)', 
                          boxShadow: 6,
                          bgcolor: selectedCategory?.id === cat.id ? 'primary.dark' : 'action.hover'
                        },
                        '&:active': {
                          transform: isMobile ? 'scale(0.98)' : 'translateY(-4px)'
                        }
                      }}
                      onClick={() => handleCategoryClick(cat)}
                    >
                      <Typography 
                        variant="subtitle1" 
                        fontWeight="bold"
                        sx={{ fontSize: { xs: '0.85rem', sm: '1rem' } }}
                      >
                        {cat.categoryName || cat.CategoryName}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>

          {selectedCategory && (
            <>
              {/* Discount Input */}
              <Box sx={{ mb: 4 }}>
                <Paper 
                  elevation={0}
                  sx={{ 
                    p: { xs: 1.5, sm: 2 }, 
                    bgcolor: alpha(theme.palette.warning.main, 0.05),
                    borderLeft: '4px solid',
                    borderColor: 'warning.main',
                    mb: { xs: 2, sm: 3 }
                  }}
                >
                  <Typography 
                    variant="h6" 
                    gutterBottom 
                    color="primary" 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1,
                      fontSize: { xs: '1rem', sm: '1.25rem' }
                    }}
                  >
                    <DiscountIcon sx={{ fontSize: { xs: 20, sm: 24 } }} /> 
                    {t('setDiscountPercentage') || 'Step 2: Set Discount'}
                  </Typography>
                </Paper>
                
                <TextField
                  label={t('discountPercentage') || 'Discount %'}
                  type="number"
                  fullWidth
                  value={discount}
                  onChange={(e) => {
                    setDiscount(e.target.value);
                    // ✅ When discount changes, clear all custom prices
                    setCustomPrices({});
                  }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    inputProps: { min: 0, max: 100 }
                  }}
                  sx={{ 
                    maxWidth: { xs: '100%', sm: 300 },
                    '& .MuiInputBase-input': {
                      fontSize: { xs: '0.9rem', sm: '1rem' }
                    }
                  }}
                  size={isMobile ? "small" : "medium"}
                  required
                  placeholder={t('enterDiscount') || 'e.g., 20'}
                />
              </Box>

              {/* Products Selection */}
              <Box>
                <Paper 
                  elevation={0}
                  sx={{ 
                    p: { xs: 1.5, sm: 2 }, 
                    bgcolor: alpha(theme.palette.success.main, 0.05),
                    borderLeft: '4px solid',
                    borderColor: 'success.main',
                    mb: { xs: 2, sm: 3 }
                  }}
                >
                  <Stack 
                    direction={{ xs: 'column', sm: 'row' }} 
                    alignItems={{ xs: 'flex-start', sm: 'center' }} 
                    justifyContent="space-between"
                    spacing={{ xs: 1, sm: 0 }}
                  >
                    <Typography 
                      variant="h6" 
                      color="primary" 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1,
                        fontSize: { xs: '1rem', sm: '1.25rem' }
                      }}
                    >
                      <InventoryIcon sx={{ fontSize: { xs: 20, sm: 24 } }} /> 
                      {t('selectProductsStep') || 'Step 3: Select Products'}
                    </Typography>
                    <Chip 
                      label={`${selectedProductIds.length} ${t('productsSelected') || 'selected'}`} 
                      color={selectedProductIds.length > 0 ? 'success' : 'default'}
                      size="small"
                      sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }}
                    />
                  </Stack>
                </Paper>

                {/* ✅ Editable price hint */}
                {selectedProductIds.length > 0 && Number(discount) > 0 && (
                  <Alert 
                    severity="info" 
                    icon={<EditIcon />}
                    sx={{ mb: 2 }}
                  >
                    <Typography variant="body2" fontWeight="bold" sx={{ fontSize: { xs: '0.8rem', sm: '0.85rem' } }}>
                      {t('editablePriceHint') || 'You can manually edit the new price for any selected product by clicking on the price field.'}
                    </Typography>
                  </Alert>
                )}

                <Paper 
                  elevation={1} 
                  sx={{ 
                    maxHeight: { xs: 400, sm: 500 }, 
                    overflow: 'auto', 
                    border: 1, 
                    borderColor: 'divider', 
                    borderRadius: 2,
                    bgcolor: '#fafafa'
                  }}
                >
                  {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
                      <CircularProgress size={isMobile ? 40 : 50} />
                    </Box>
                  ) : products.length > 0 ? (
                    <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
                      {products.map((p) => {
                        const oldPrice = p.oldPrice || p.OldPrice || p.price || p.Price || 0;
                        const calculatedPrice = calculateNewPrice(oldPrice);
                        const productId = p.id || p.Id;
                        const effectivePrice = getEffectiveNewPrice(p);
                        const isCustom = hasCustomPrice(productId);
                        const isSelected = selectedProductIds.includes(productId);
                        const productImagesCount = getProductImagesCount(p);
                        const productColorsCount = getProductColorsCount(p);
                        
                        return (
                          <Paper
                            key={productId}
                            elevation={isSelected ? 3 : 1}
                            sx={{ 
                              p: { xs: 1.5, sm: 2 }, 
                              mb: { xs: 1.5, sm: 2 }, 
                              bgcolor: isSelected 
                                ? isCustom 
                                  ? alpha(theme.palette.warning.main, 0.08)
                                  : alpha(theme.palette.success.main, 0.1) 
                                : 'background.paper',
                              borderRadius: 2,
                              transition: 'all 0.3s ease',
                              border: isSelected ? '2px solid' : '1px solid transparent',
                              borderColor: isCustom ? 'warning.main' : 'success.main',
                              '&:hover': {
                                boxShadow: 4
                              }
                            }}
                          >
                            <Stack 
                              direction={{ xs: 'column', sm: 'row' }} 
                              alignItems={{ xs: 'flex-start', sm: 'center' }}
                              justifyContent="space-between"
                              spacing={{ xs: 1.5, sm: 0 }}
                            >
                              <Stack direction="row" alignItems="center" spacing={1} sx={{ flexGrow: 1, flexWrap: 'wrap' }}>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={isSelected}
                                      onChange={() => handleProductToggle(productId)}
                                      color="success"
                                      size={isMobile ? "small" : "medium"}
                                    />
                                  }
                                  label={
                                    <Typography 
                                      variant="body1" 
                                      fontWeight="bold"
                                      sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
                                    >
                                      {p.productName || p.ProductName}
                                    </Typography>
                                  }
                                  sx={{ flexGrow: 1, m: 0 }}
                                />
                                
                                {/* Show ProductImages indicator */}
                                {productImagesCount > 0 && (
                                  <Tooltip title={`${productImagesCount} ${t('additionalImages') || 'additional images will be copied'}`}>
                                    <Chip
                                      icon={<CollectionsIcon sx={{ fontSize: { xs: 12, sm: 14 } }} />}
                                      label={productImagesCount}
                                      size="small"
                                      color="info"
                                      sx={{ 
                                        height: { xs: 20, sm: 24 },
                                        fontSize: { xs: '0.7rem', sm: '0.75rem' }
                                      }}
                                    />
                                  </Tooltip>
                                )}
                                
                                {/* Show ProductColors indicator */}
                                {productColorsCount > 0 && (
                                  <Tooltip title={`${productColorsCount} ${t('productColors') || 'colors will be copied'}`}>
                                    <Chip
                                      icon={<PaletteIcon sx={{ fontSize: { xs: 12, sm: 14 } }} />}
                                      label={productColorsCount}
                                      size="small"
                                      color="secondary"
                                      sx={{ 
                                        height: { xs: 20, sm: 24 },
                                        fontSize: { xs: '0.7rem', sm: '0.75rem' }
                                      }}
                                    />
                                  </Tooltip>
                                )}

                                {/* ✅ Show "edited" badge if price was manually changed */}
                                {isCustom && isSelected && (
                                  <Chip
                                    icon={<EditIcon sx={{ fontSize: { xs: 12, sm: 14 } }} />}
                                    label={t('manuallyEdited') || 'Edited'}
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                    sx={{ 
                                      height: { xs: 20, sm: 24 },
                                      fontSize: { xs: '0.7rem', sm: '0.75rem' }
                                    }}
                                  />
                                )}
                              </Stack>
                              
                              <Stack 
                                direction="row" 
                                spacing={{ xs: 1.5, sm: 2 }} 
                                alignItems="center"
                                sx={{ width: { xs: '100%', sm: 'auto' } }}
                              >
                                {/* Old Price */}
                                <Box sx={{ textAlign: 'center', flex: { xs: 1, sm: 'none' } }}>
                                  <Typography 
                                    variant="caption" 
                                    color="text.secondary"
                                    sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                  >
                                    {t('oldPrice') || 'Old'}
                                  </Typography>
                                  <Typography 
                                    variant="body1" 
                                    sx={{ 
                                      textDecoration: 'line-through', 
                                      color: 'error.main',
                                      fontSize: { xs: '0.85rem', sm: '1rem' }
                                    }}
                                  >
                                    {oldPrice}
                                  </Typography>
                                </Box>

                                {/* ✅ Calculated price (shown when custom price exists) */}
                                {isCustom && isSelected && (
                                  <Box sx={{ textAlign: 'center', flex: { xs: 1, sm: 'none' } }}>
                                    <Typography 
                                      variant="caption" 
                                      color="text.secondary"
                                      sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' } }}
                                    >
                                      {t('calculatedPrice') || 'Calc.'}
                                    </Typography>
                                    <Typography 
                                      variant="body2" 
                                      sx={{ 
                                        color: 'text.secondary',
                                        fontSize: { xs: '0.75rem', sm: '0.85rem' },
                                        textDecoration: 'line-through',
                                        opacity: 0.7
                                      }}
                                    >
                                      {calculatedPrice}
                                    </Typography>
                                  </Box>
                                )}
                                
                                {/* ✅ FIX: Editable New Price */}
                                <Box sx={{ 
                                  textAlign: 'center', 
                                  bgcolor: isCustom 
                                    ? alpha(theme.palette.warning.main, 0.15) 
                                    : 'success.main', 
                                  color: isCustom ? 'text.primary' : 'white', 
                                  px: { xs: 1, sm: 1.5 }, 
                                  py: { xs: 0.5, sm: 0.8 }, 
                                  borderRadius: 1,
                                  flex: { xs: 1, sm: 'none' },
                                  border: isCustom ? '2px solid' : 'none',
                                  borderColor: 'warning.main',
                                  minWidth: { xs: 100, sm: 130 }
                                }}>
                                  <Typography 
                                    variant="caption"
                                    sx={{ 
                                      fontSize: { xs: '0.65rem', sm: '0.7rem' },
                                      color: isCustom ? 'warning.dark' : 'inherit',
                                      fontWeight: isCustom ? 'bold' : 'normal',
                                      display: 'block',
                                      mb: 0.3
                                    }}
                                  >
                                    {isCustom 
                                      ? (t('editedPrice') || '✏️ Edited') 
                                      : (t('newPrice') || 'New')
                                    }
                                  </Typography>
                                  
                                  {isSelected && Number(discount) > 0 ? (
                                    <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5}>
                                      <TextField
                                        type="number"
                                        value={effectivePrice}
                                        onChange={(e) => {
                                          const val = e.target.value;
                                          // ✅ FIX: Always store the typed value in customPrices
                                          // even if it matches calculated, so we know user edited it
                                          handleCustomPriceChange(productId, val);
                                        }}
                                        onBlur={() => {
                                          // ✅ FIX: On blur, if the value matches calculated price exactly, 
                                          // keep it as custom so user intent is preserved
                                          const currentVal = customPrices[productId];
                                          if (currentVal === '' || currentVal === undefined || currentVal === null) {
                                            // If empty, remove custom price
                                            resetToCalculatedPrice(p);
                                          }
                                        }}
                                        variant="standard"
                                        size="small"
                                        inputProps={{ 
                                          min: 0,
                                          step: 0.01,
                                          style: { 
                                            textAlign: 'center', 
                                            fontWeight: 'bold',
                                            fontSize: isMobile ? '0.95rem' : '1.1rem',
                                            color: isCustom ? theme.palette.warning.dark : 'white',
                                            padding: '2px 0'
                                          }
                                        }}
                                        sx={{
                                          width: { xs: 65, sm: 75 },
                                          '& .MuiInput-underline:before': {
                                            borderBottomColor: isCustom ? 'warning.main' : 'rgba(255,255,255,0.5)'
                                          },
                                          '& .MuiInput-underline:hover:before': {
                                            borderBottomColor: isCustom ? 'warning.dark' : 'white'
                                          },
                                          '& .MuiInput-underline:after': {
                                            borderBottomColor: isCustom ? 'warning.dark' : 'white'
                                          }
                                        }}
                                      />
                                      {isCustom && (
                                        <Tooltip title={t('resetToCalculated') || 'Reset to calculated price'}>
                                          <IconButton 
                                            size="small" 
                                            onClick={() => resetToCalculatedPrice(p)}
                                            sx={{ 
                                              p: 0.3,
                                              color: 'warning.dark',
                                              '&:hover': { bgcolor: alpha(theme.palette.warning.main, 0.2) }
                                            }}
                                          >
                                            <RefreshIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                    </Stack>
                                  ) : (
                                    <Typography 
                                      variant="h6" 
                                      fontWeight="bold"
                                      sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                                    >
                                      {effectivePrice}
                                    </Typography>
                                  )}
                                </Box>
                              </Stack>
                            </Stack>
                          </Paper>
                        );
                      })}
                    </Box>
                  ) : (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                      <InventoryIcon sx={{ fontSize: { xs: 50, sm: 60 }, color: 'grey.300', mb: 2 }} />
                      <Typography 
                        color="text.secondary"
                        sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}
                      >
                        {t('noProductsFoundInCategory') || 'No products in this category'}
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </Box>

              {/* Action Buttons */}
              <Stack 
                direction={{ xs: 'column', sm: 'row' }} 
                justifyContent="flex-end" 
                spacing={{ xs: 1.5, sm: 2 }}
                sx={{ mt: { xs: 3, sm: 4 } }}
              >
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => {
                    setSelectedProductIds([]);
                    setDiscount(0);
                    setCustomPrices({});
                  }}
                  disabled={selectedProductIds.length === 0}
                  fullWidth={isMobile}
                  size={isMobile ? "medium" : "large"}
                  sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}
                >
                  {t('clearSelection') || 'Clear Selection'}
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  size={isMobile ? "medium" : "large"}
                  startIcon={<SaveIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />}
                  onClick={handleSubmit}
                  disabled={selectedProductIds.length === 0 || !discount}
                  fullWidth={isMobile}
                  sx={{ 
                    px: { xs: 3, sm: 4 }, 
                    py: { xs: 1.2, sm: 1.5 }, 
                    fontWeight: 'bold',
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    boxShadow: 3,
                    '&:hover': {
                      boxShadow: 6
                    }
                  }}
                >
                  {t('applyOffer') || 'Apply Offer'}
                </Button>
              </Stack>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default Offers;